import { useCallback, useMemo } from 'react'
import { notification } from 'antd'
import isNil from 'lodash/isNil'
import {
  useRevokeCompanyAccessMutation,
  useRosterByEmailQuery,
} from '../../api/user-invitation-api-client'
import { RevokeCompanyAccessAction } from '../../api/dto/user-invitation.dto'
import { extractError } from '../../api/extract-error'
import { useFundingInviteAccess } from '../UserManagement/useFundingInviteAccess'
import { useBillingProfilesQuery } from '../../api/client-billing-profile-api-client'
import { BillingProfileStatus } from './types'
import { planRecipientRemoval } from './recipientRemoval'

interface UseRecipientRemovalParams {
  open: boolean
  /** The company being edited; null while the modal has no profile. */
  clientPublicKey: string | null
  /** Named in each person's line. */
  companyName: string
  /** The profile being edited, so its siblings can be told from itself. */
  profileId: number | null
  /** Addresses the form has taken off the profile. */
  removedEmails: string[]
  /** What the write was, which the revoke reports against: 'Profile saved'. */
  doneLabel: string
}

/** A company rarely has more than a couple of profiles; this is the ceiling. */
const SIBLING_PROFILE_LIMIT = 50

const key = (email: string): string => email.trim().toLowerCase()

/**
 * What taking addresses off a company does to each person, and the company
 * revoke that goes with the save. See planRecipientRemoval for the rule.
 */
export const useRecipientRemoval = ({
  open,
  clientPublicKey,
  companyName,
  profileId,
  removedEmails,
  doneLabel,
}: UseRecipientRemovalParams) => {
  const inviteAccess = useFundingInviteAccess()
  const asking = open && removedEmails.length > 0
  // Read whether or not this admin may revoke: they still see what stops.
  const { data, isLoading: isRosterLoading } = useRosterByEmailQuery(
    removedEmails,
    { enabled: asking }
  )
  // The roster counts a company once however many profiles bill it, so an
  // address a sibling profile still lists keeps the company.
  const { data: siblingProfiles, isLoading: isSiblingsLoading } =
    useBillingProfilesQuery(
      {
        clientPublicKey: clientPublicKey ?? undefined,
        status: BillingProfileStatus.Active,
        limit: SIBLING_PROFILE_LIMIT,
      },
      { enabled: asking && !isNil(clientPublicKey) }
    )
  const revokeMutation = useRevokeCompanyAccessMutation()

  const stillInvoiced = useMemo(
    () =>
      new Set(
        (siblingProfiles?.data ?? [])
          .filter((profile) => profile.id !== profileId)
          .flatMap((profile) => [
            ...(profile.toEmails ?? []),
            ...(profile.ccEmails ?? []),
          ])
          .map(key)
      ),
    [siblingProfiles, profileId]
  )

  const plan = useMemo(
    () =>
      planRecipientRemoval({
        removedEmails,
        rows: data?.data ?? [],
        clientPublicKey,
        companyName,
        stillInvoiced,
        canRevoke: inviteAccess.allowed,
      }),
    [
      removedEmails,
      data,
      clientPublicKey,
      companyName,
      stillInvoiced,
      inviteAccess.allowed,
    ]
  )

  /**
   * Runs after the profile write, so the server sees the address already gone
   * when it decides whether Finance Manager comes off.
   */
  const revoke = useCallback(async () => {
    if (plan.revocable.length === 0 || isNil(clientPublicKey)) return

    const describe = (items: { email: string; reason?: string | null }[]) =>
      items.map((item) => `${item.email}: ${item.reason ?? 'No reason given'}`)

    try {
      const result = await revokeMutation.mutateAsync({
        revocations: plan.revocable.map((email) => ({
          email,
          personId: null,
          clientPublicKey,
          roleIds: inviteAccess.roleIds,
        })),
      })

      const failed = result.results.filter(
        (item) => item.action === RevokeCompanyAccessAction.Failed
      )
      if (failed.length > 0) {
        notification.error({
          message: `${doneLabel}, access not removed`,
          description: describe(failed).join('\n'),
        })
      }

      // A skip means the server kept the access, so staying quiet would read
      // as removed.
      const skipped = result.results.filter(
        (item) => item.action === RevokeCompanyAccessAction.Skipped
      )
      if (skipped.length > 0) {
        notification.warning({
          message: `${doneLabel}, access left in place`,
          description: describe(skipped).join('\n'),
        })
      }
    } catch (error) {
      notification.error({
        message: `${doneLabel}, access not removed`,
        description: extractError(error, 'The access could not be removed'),
      })
    }
  }, [
    plan.revocable,
    clientPublicKey,
    inviteAccess.roleIds,
    doneLabel,
    revokeMutation.mutateAsync,
  ])

  return {
    lines: plan.lines,
    /** False when this admin may not change access; only invoices stop. */
    canRevoke: inviteAccess.allowed,
    /** Keeps the button spinning through the revoke, not just the write. */
    isRevoking: revokeMutation.isPending,
    /** Still loading, so the lines are not final and confirming waits. */
    isChecking: asking && (isRosterLoading || isSiblingsLoading),
    revoke,
  }
}
